import { useState, useCallback, memo } from 'react';
import { Stack, Image, Text, Button } from '@chakra-ui/react';
import { PencilSimpleLine } from '@phosphor-icons/react';

import meetingNotStartedImage from '@/assets/image/meeting-not-started-cover.svg';
import { AgencyPlan } from '@/types/enums';
import { Job } from '@/types/Job';
import { CandidateWithJobContext } from '@/types/Candidate';
import { Meeting, MeetingSummary } from '@/types/Meeting';
import AddInterviewDialog from '@/views/Interview/Copilot/AddInterviewDialog';

interface InterviewNotStartedProps {
  meeting: Meeting;
  candidate: CandidateWithJobContext;
  job: Job;
  onEditInterview: (updatedMeeting: MeetingSummary) => void;
}

const InterviewNotStarted = memo(
  ({ meeting, candidate, job, onEditInterview }: InterviewNotStartedProps) => {
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

    const getMeetingTime = useCallback(() => {
      return new Date(meeting.meeting_start_time * 1000);
    }, [meeting.meeting_start_time]);

    return (
      <Stack align="center" mt="64px" mb="32px">
        <Image w="210px" h="210px" src={meetingNotStartedImage} alt="Empty Interview" mb="60px" />
        <Text fontSize="24px" fontWeight="bold" color="neutrals.800" mb="8px">
          Interview not started yet
        </Text>
        <Text fontSize="18px" color="neutrals.500" mb="24px">
          Your interview is set for{' '}
          {getMeetingTime().toLocaleTimeString('default', {
            hour: 'numeric',
            minute: 'numeric'
          })}{' '}
          on{' '}
          {getMeetingTime().toLocaleDateString('default', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          })}
          .
        </Text>
        <Button
          color="neutrals.0"
          bg="primary.900"
          borderRadius="8px"
          fontWeight="semibold"
          fontSize="14px"
          p="10px 24px"
          _hover={{ bg: 'primary.700' }}
          aria-label="Edit interview"
          onClick={() => setIsEditDialogOpen(true)}
        >
          Edit Meeting Details
          <PencilSimpleLine size={24} aria-hidden="true" />
        </Button>
        <AddInterviewDialog
          mode="edit"
          meeting={{
            meeting_id: meeting.meeting_id,
            title: meeting.title,
            meeting_url: meeting.meeting_url,
            meeting_start_time: meeting.meeting_start_time,
            meeting_end_time: meeting.meeting_end_time,
            timestamp: meeting.timestamp,
            status: meeting.status,
            candidate: {
              candidate_id: candidate.candidate_id,
              job: {
                job_id: job.job_id,
                job_title: job.job_title,
                location: job.location,
                created_at: job.created_at,
                status: job.status,
                unique_id: job.unique_id,
                work_type: job.work_type,
                employment_type: job.employment_type,
                client: job.client,
                archived_at: 0,
                candidates: [],
                category: '',
                publish_job_board: false
              },
              first_name: candidate.first_name,
              last_name: candidate.last_name,
              email: candidate.email,
              phone: candidate.phone,
              linkedin: candidate.linkedin,
              resume_data: candidate.resume_data,
              status: candidate.status,
              created_at: candidate.created_at,
              user_score: {
                resume_score: 0,
                interview_score: 0,
                overall_score: 0
              },
              system_score: 0,
              overall_score: 0,
              original_resume_file_name: '',
              unique_id: '',
              experience_level: candidate.experience_level,
              candidate_status: candidate.candidate_status
            }
          }}
          onEditInterview={onEditInterview}
          isOpen={isEditDialogOpen}
          onClose={() => setIsEditDialogOpen(false)}
          plan={AgencyPlan.FREE}
        />
      </Stack>
    );
  }
);

InterviewNotStarted.displayName = 'InterviewNotStarted';

export default InterviewNotStarted;
